import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Plus, Trash2, GitFork, PlayCircle,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { cn }              from '@/utils/cn';
import { apiClient }       from '@/api/client';
import { AccountLookupField } from '@/components/erp/AccountLookupField';

// ── Types ─────────────────────────────────────────────────────────────────────
interface GlAllocationTemplateLineForm {
  _key: string;
  targetAccountCode: string;
  targetAccountId:   string;
  targetAccountName: string;
  allocationPercentage: string;
  allocationAmount:     string;
  description:          string;
}

interface GlAllocationTemplateFormState {
  templateCode:      string;
  description:       string;
  sourceAccountCode: string;
  sourceAccountId:   string;
  sourceAccountName: string;
  allocationMethod:  string;
  notes:             string;
  lines:             GlAllocationTemplateLineForm[];
}

interface GlAllocationTemplateDto {
  id: string;
  templateCode: string;
  description: string;
  sourceAccountId: string;
  sourceAccountCode: string;
  allocationMethod: string;
  isActive: boolean;
  notes?: string;
  totalPercentage: number;
  isBalanced: boolean;
  lines: Array<{
    id: string;
    lineNumber: number;
    targetAccountId: string;
    targetAccountCode: string;
    targetAccountName: string;
    allocationPercentage?: number;
    allocationAmount?: number;
    description?: string;
  }>;
  createdDate: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const METHODS: Record<string, string> = {
  Percentage:  'Fixed Percentage',
  FixedAmount: 'Fixed Amount',
};

const makeKey = () => Math.random().toString(36).slice(2, 10);

const emptyLine = (): GlAllocationTemplateLineForm => ({
  _key: makeKey(),
  targetAccountCode:    '',
  targetAccountId:      '',
  targetAccountName:    '',
  allocationPercentage: '',
  allocationAmount:     '',
  description:          '',
});

const defaultForm = (): GlAllocationTemplateFormState => ({
  templateCode:      '',
  description:       '',
  sourceAccountCode: '',
  sourceAccountId:   '',
  sourceAccountName: '',
  allocationMethod:  'Percentage',
  notes:             '',
  lines:             [emptyLine()],
});

// ── Helpers ───────────────────────────────────────────────────────────────────
const inputCls = cn(
  'w-full text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-shadow',
);

const LabeledField = ({
  label, required, error, children,
}: { label: string; required?: boolean; error?: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

// ── Execute Modal ─────────────────────────────────────────────────────────────
const ExecuteModal = ({
  onClose, onExecute, executing,
}: { onClose: () => void; onExecute: (period: string, date: string) => void; executing: boolean }) => {
  const [period, setPeriod] = useState('');
  const [date,   setDate]   = useState(new Date().toISOString().split('T')[0]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-sm">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <PlayCircle size={18} className="text-teal-600 dark:text-teal-400" /> Execute Allocation
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">
            The source account balance for the selected period will be distributed across target accounts and a GL journal will be created.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                Financial Period Code <span className="text-red-500">*</span>
              </label>
              <input
                value={period}
                onChange={e => setPeriod(e.target.value.toUpperCase())}
                placeholder="e.g. FY2026-01"
                className={inputCls}
                autoFocus
              />
              <p className="mt-1 text-xs text-slate-400">Enter the period whose source-account balance will be distributed.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Execution Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>
        <div className="flex gap-2 px-6 pb-6 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-gray-400 border border-slate-200 dark:border-gray-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onExecute(period, date)}
            disabled={!period.trim() || executing}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60 transition-colors"
          >
            {executing ? <Spinner size="sm" /> : <PlayCircle size={14} />}
            {executing ? 'Executing…' : 'Execute'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
export const GlAllocationTemplateFormPage = () => {
  const { id }            = useParams<{ id: string }>();
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const isEdit            = Boolean(id);

  const canSave = isEdit
    ? hasPermission(Permissions.GL_ALLOCATION_EDIT)
    : hasPermission(Permissions.GL_ALLOCATION_CREATE);
  const canExecute = hasPermission(Permissions.GL_ALLOCATION_EXECUTE);

  const [form,          setForm]          = useState<GlAllocationTemplateFormState>(defaultForm());
  const [loading,       setLoading]       = useState(isEdit);
  const [saving,        setSaving]        = useState(false);
  const [executing,     setExecuting]     = useState(false);
  const [showExecModal, setShowExecModal] = useState(false);
  const [errors,        setErrors]        = useState<Record<string, string>>({});
  const [apiError,      setApiError]      = useState<string | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setApiError(null);
    try {
      const res = await apiClient.get(`/gl-allocation-templates/${id}`);
      const dto: GlAllocationTemplateDto = res.data?.data;
      if (!dto) return;
      setForm({
        templateCode:      dto.templateCode,
        description:       dto.description,
        sourceAccountCode: dto.sourceAccountCode,
        sourceAccountId:   dto.sourceAccountId,
        sourceAccountName: (dto as { sourceAccountName?: string }).sourceAccountName ?? '',
        allocationMethod:  dto.allocationMethod,
        notes:             dto.notes ?? '',
        lines: dto.lines.map(l => ({
          _key:                 makeKey(),
          targetAccountCode:    l.targetAccountCode,
          targetAccountId:      l.targetAccountId,
          targetAccountName:    l.targetAccountName,
          allocationPercentage: l.allocationPercentage != null ? String(l.allocationPercentage) : '',
          allocationAmount:     l.allocationAmount     != null ? String(l.allocationAmount)     : '',
          description:          l.description ?? '',
        })),
      });
    } catch {
      setApiError('Failed to load template.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (id) load(); }, [id, load]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const setF = (key: keyof Omit<GlAllocationTemplateFormState, 'lines'>, val: string) =>
    setForm(f => ({ ...f, [key]: val }));

  const setLine = (key: string, field: keyof GlAllocationTemplateLineForm, val: string) =>
    setForm(f => ({ ...f, lines: f.lines.map(l => l._key === key ? { ...l, [field]: val } : l) }));

  const addLine    = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (key: string) => setForm(f => ({ ...f, lines: f.lines.filter(l => l._key !== key) }));

  // ── Account selection (searchable lookup, no code typed by heart) ──────────
  const setSourceAccount = (accId: string | null, code: string | null, name: string | null) => {
    setForm(f => ({ ...f, sourceAccountId: accId ?? '', sourceAccountCode: code ?? '', sourceAccountName: name ?? '' }));
    setErrors(e => ({ ...e, sourceAccount: '' }));
  };

  const setLineAccount = (lineKey: string, accId: string | null, code: string | null, name: string | null) => {
    setForm(f => ({
      ...f,
      lines: f.lines.map(l =>
        l._key === lineKey
          ? { ...l, targetAccountId: accId ?? '', targetAccountCode: code ?? '', targetAccountName: name ?? '' }
          : l,
      ),
    }));
  };

  // ── Totals ─────────────────────────────────────────────────────────────────
  const totalPct = form.lines.reduce((s, l) => s + (parseFloat(l.allocationPercentage) || 0), 0);
  const isPctBalanced = Math.abs(totalPct - 100) < 0.01;

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.templateCode.trim())      e.templateCode  = 'Template code is required';
    if (!form.description.trim())       e.description   = 'Description is required';
    if (!form.sourceAccountCode.trim()) e.sourceAccount = 'Source account is required';
    if (!form.sourceAccountId)          e.sourceAccount = 'Invalid source account — tab out to validate';
    if (form.lines.length === 0)        e.lines         = 'At least one allocation line is required';

    for (const l of form.lines) {
      if (!l.targetAccountId) {
        e.lines = 'All target accounts must be valid — tab out of each account field to validate'; break;
      }
      if (form.allocationMethod === 'Percentage') {
        const pct = parseFloat(l.allocationPercentage);
        if (isNaN(pct) || pct <= 0) { e.lines = 'All allocation percentages must be > 0'; break; }
      } else {
        const amt = parseFloat(l.allocationAmount);
        if (isNaN(amt) || amt <= 0) { e.lines = 'All allocation amounts must be > 0'; break; }
      }
    }

    if (form.allocationMethod === 'Percentage' && !isPctBalanced && !e.lines) {
      e.lines = `Percentages must total 100% (currently ${totalPct.toFixed(2)}%)`;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError(null);

    const linePayloads = form.lines.map(l => ({
      targetAccountId:      l.targetAccountId,
      targetAccountCode:    l.targetAccountCode,
      targetAccountName:    l.targetAccountName,
      allocationPercentage: form.allocationMethod === 'Percentage'  ? parseFloat(l.allocationPercentage) : null,
      allocationAmount:     form.allocationMethod === 'FixedAmount' ? parseFloat(l.allocationAmount)     : null,
      description:          l.description.trim() || null,
    }));

    try {
      if (isEdit) {
        await apiClient.put(`/gl-allocation-templates/${id}`, {
          description:      form.description.trim(),
          allocationMethod: form.allocationMethod,
          notes:            form.notes.trim() || null,
          lines:            linePayloads,
        });
      } else {
        await apiClient.post('/gl-allocation-templates', {
          templateCode:      form.templateCode.trim().toUpperCase(),
          description:       form.description.trim(),
          sourceAccountId:   form.sourceAccountId,
          sourceAccountCode: form.sourceAccountCode,
          allocationMethod:  form.allocationMethod,
          notes:             form.notes.trim() || null,
          lines:             linePayloads,
        });
      }
      navigate(AppRoutes.GL_ALLOCATION_TEMPLATES_LIST);
    } catch (err: any) {
      setApiError(
        err.response?.data?.errors?.[0]?.message
        ?? err.response?.data?.message
        ?? err.response?.data?.title
        ?? 'Save failed. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Execute ────────────────────────────────────────────────────────────────
  const handleExecute = async (periodCode: string, execDate: string) => {
    if (!id) return;
    setExecuting(true);
    setApiError(null);
    try {
      await apiClient.post(`/gl-allocation-templates/${id}/execute`, {
        financialPeriodCode: periodCode.trim().toUpperCase(),
        executionDate:       execDate || null,
      });
      setShowExecModal(false);
      alert('Allocation executed successfully. GL journal entry has been created.');
    } catch (err: any) {
      setShowExecModal(false);
      setApiError(err.response?.data?.message ?? 'Execution failed. Please check the period code and try again.');
    } finally {
      setExecuting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="flex h-full items-center justify-center"><Spinner size="lg" /></div>;
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(AppRoutes.GL_ALLOCATION_TEMPLATES_LIST)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <GitFork size={20} className="text-teal-600 dark:text-teal-400" />
              {isEdit ? 'Edit GL Allocation Template' : 'New GL Allocation Template'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {isEdit && canExecute && (
              <button
                onClick={() => setShowExecModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
              >
                <PlayCircle size={15} /> Execute
              </button>
            )}
            {canSave && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors"
              >
                {saving ? <Spinner size="sm" /> : <Save size={15} />}
                {saving ? 'Saving…' : 'Save Template'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-6 space-y-5">
        {apiError && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {apiError}
          </div>
        )}

        {/* Template Information */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Template Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <LabeledField label="Template Code" required error={errors.templateCode}>
              <input
                value={form.templateCode}
                onChange={e => setF('templateCode', e.target.value.toUpperCase())}
                placeholder="e.g. ALLOC-001"
                disabled={isEdit}
                className={cn(inputCls, errors.templateCode ? 'border-red-400 focus:ring-red-400' : '', isEdit && 'opacity-70 cursor-not-allowed')}
              />
              {isEdit && <p className="mt-1 text-xs text-slate-400">Template code cannot be changed after creation.</p>}
            </LabeledField>

            <LabeledField label="Description" required error={errors.description}>
              <input
                value={form.description}
                onChange={e => setF('description', e.target.value)}
                placeholder="e.g. Overhead allocation by department"
                className={cn(inputCls, errors.description ? 'border-red-400 focus:ring-red-400' : '')}
              />
            </LabeledField>

            <LabeledField label="Allocation Method" required>
              <select value={form.allocationMethod} onChange={e => setF('allocationMethod', e.target.value)} className={inputCls}>
                {Object.entries(METHODS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </LabeledField>

            <LabeledField label="Source Account (account to distribute from)" required error={errors.sourceAccount}>
              <AccountLookupField
                label=""
                value={form.sourceAccountId || null}
                displayCode={form.sourceAccountCode}
                displayName={form.sourceAccountName}
                isPostingAccount
                disabled={isEdit}
                placeholder="Search account…"
                onChange={setSourceAccount}
              />
              {isEdit && <p className="mt-1 text-xs text-slate-400">Source account cannot be changed after creation.</p>}
            </LabeledField>

            <LabeledField label="Notes">
              <input
                value={form.notes}
                onChange={e => setF('notes', e.target.value)}
                placeholder="Optional notes about this template"
                className={inputCls}
              />
            </LabeledField>
          </div>
        </div>

        {/* Allocation Lines */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Allocation Lines</h2>
              {form.allocationMethod === 'Percentage' && (
                <p className={cn('text-xs mt-0.5 font-medium', isPctBalanced ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400')}>
                  Total: {totalPct.toFixed(2)}%{isPctBalanced ? ' ✓ Balanced' : ' — must equal 100%'}
                </p>
              )}
            </div>
            <button
              onClick={addLine}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-700 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
            >
              <Plus size={14} /> Add Line
            </button>
          </div>

          {errors.lines && (
            <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-400">
              {errors.lines}
            </div>
          )}

          <div className="overflow-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700">
                  <th className="py-2 px-2 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase w-32">Target Account</th>
                  <th className="py-2 px-2 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Account Name</th>
                  <th className="py-2 px-2 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase w-28">
                    {form.allocationMethod === 'Percentage' ? 'Percentage %' : 'Amount'}
                  </th>
                  <th className="py-2 px-2 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Description</th>
                  <th className="py-2 px-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                {form.lines.map((line, idx) => (
                  <tr key={line._key} className="group">
                    <td className="py-1.5 px-2 min-w-[220px]">
                      <AccountLookupField
                        label=""
                        value={line.targetAccountId || null}
                        displayCode={line.targetAccountCode}
                        displayName={line.targetAccountName}
                        isPostingAccount
                        placeholder="Search account…"
                        onChange={(accId, code, name) => setLineAccount(line._key, accId, code, name)}
                      />
                    </td>
                    <td className="py-1.5 px-2 min-w-[160px]">
                      <span className="text-xs text-slate-600 dark:text-slate-300">
                        {line.targetAccountName || <span className="text-slate-400 italic">—</span>}
                      </span>
                    </td>
                    <td className="py-1.5 px-2">
                      {form.allocationMethod === 'Percentage' ? (
                        <input
                          type="number"
                          value={line.allocationPercentage}
                          onChange={e => setLine(line._key, 'allocationPercentage', e.target.value)}
                          placeholder="0.00"
                          min={0} max={100} step={0.01}
                          className={cn(inputCls, 'text-xs text-right')}
                        />
                      ) : (
                        <input
                          type="number"
                          value={line.allocationAmount}
                          onChange={e => setLine(line._key, 'allocationAmount', e.target.value)}
                          placeholder="0.00"
                          min={0} step={0.01}
                          className={cn(inputCls, 'text-xs text-right')}
                        />
                      )}
                    </td>
                    <td className="py-1.5 px-2">
                      <input
                        value={line.description}
                        onChange={e => setLine(line._key, 'description', e.target.value)}
                        placeholder="Optional"
                        className={cn(inputCls, 'text-xs')}
                      />
                    </td>
                    <td className="py-1.5 px-2 text-center">
                      <button
                        onClick={() => removeLine(line._key)}
                        disabled={form.lines.length <= 1}
                        title={`Remove line ${idx + 1}`}
                        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-400 disabled:opacity-20 transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {form.allocationMethod === 'Percentage' && form.lines.length > 0 && (
                <tfoot>
                  <tr className="border-t border-slate-200 dark:border-slate-600">
                    <td colSpan={2} className="py-2 px-2 text-xs font-semibold text-slate-500 dark:text-gray-400">Total</td>
                    <td className={cn('py-2 px-2 text-xs font-bold text-right',
                      isPctBalanced ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                    )}>
                      {totalPct.toFixed(2)}%
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <p className="mt-3 text-xs text-slate-400">
            Enter account codes and press Tab to auto-validate against the chart of accounts.
            {form.allocationMethod === 'Percentage' && ' Percentages must sum to exactly 100%.'}
          </p>
        </div>
      </div>

      {/* Execute Modal */}
      {showExecModal && (
        <ExecuteModal
          onClose={() => setShowExecModal(false)}
          onExecute={handleExecute}
          executing={executing}
        />
      )}
    </div>
  );
};
